import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { filledSheets, isEmpty, readWorkbook, type CellInfo, type SheetInfo } from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";

/**
 * Ist die Spalte durchgehend auf- oder absteigend geordnet?
 *
 * Verglichen wird typgerecht: Zahlen und Datumswerte nach Größe, Text
 * alphabetisch ohne Rücksicht auf Groß- und Kleinschreibung – so, wie Excel
 * selbst sortiert. Weniger als drei Werte gelten nicht als Nachweis.
 */
function istGeordnet(werte: CellInfo[]): "aufsteigend" | "absteigend" | null {
  if (werte.length < 3) return null;

  const vergleich = (a: CellInfo, b: CellInfo): number => {
    if (typeof a.value === "number" && typeof b.value === "number") return a.value - b.value;
    return String(a.value).toLocaleLowerCase("de").localeCompare(String(b.value).toLocaleLowerCase("de"), "de");
  };

  let aufsteigend = true;
  let absteigend = true;
  for (let i = 1; i < werte.length; i += 1) {
    const abstand = vergleich(werte[i - 1], werte[i]);
    if (abstand > 0) aufsteigend = false;
    if (abstand < 0) absteigend = false;
  }
  if (aufsteigend && absteigend) return null; // alle Werte gleich – kein Nachweis
  if (aufsteigend) return "aufsteigend";
  if (absteigend) return "absteigend";
  return null;
}

/** Spalten eines Blattes ohne die Kopfzeile, jeweils als Liste gefüllter Zellen. */
function datenspalten(blatt: SheetInfo): Array<{ spalte: number; kopf: string; werte: CellInfo[] }> {
  const gefuellt = blatt.cells.filter((zelle) => !isEmpty(zelle));
  if (gefuellt.length === 0) return [];

  const ersteZeile = Math.min(...gefuellt.map((zelle) => zelle.row));
  const spalten = [...new Set(gefuellt.map((zelle) => zelle.column))].sort((a, b) => a - b);

  return spalten.map((spalte) => {
    const inSpalte = gefuellt
      .filter((zelle) => zelle.column === spalte)
      .sort((a, b) => a.row - b.row);
    const kopfZelle = inSpalte.find((zelle) => zelle.row === ersteZeile);
    return {
      spalte,
      kopf: kopfZelle ? String(kopfZelle.value) : `Spalte ${spalte}`,
      werte: inSpalte.filter((zelle) => zelle.row > ersteZeile),
    };
  });
}

export const lektionSortieren: Lesson = {
  id: "sortieren",
  title: "Sortieren – und der Fehler, der alles zerlegt",
  summary: "Eine einzelne Spalte zu sortieren, ist die schnellste Art, einen Datensatz unbrauchbar zu machen.",
  durationMinutes: 10,
  learningGoals: [
    "Erklären, warum eine Spalte nie allein sortiert werden darf",
    "Nach mehreren Kriterien nacheinander sortieren",
    "Nach Farbe und nach eigener Reihenfolge sortieren",
  ],
  steps: [
    {
      id: "die-falle",
      title: "Der teuerste Klick in Excel",
      explanation: [
        "Man markiert eine Spalte, klickt auf „Aufsteigend sortieren“ – und hat gerade " +
          "die Zuordnung zwischen allen Spalten zerstört. Die Namen stehen jetzt in " +
          "neuer Reihenfolge, die Beträge daneben nicht. Jede Zeile enthält Daten von " +
          "zwei verschiedenen Vorgängen, und es sieht vollkommen normal aus.",
        "Excel warnt davor, wenn es die Situation erkennt: Es fragt, ob die Auswahl " +
          "erweitert werden soll. Die Antwort ist praktisch immer ja. Wer stattdessen " +
          "„mit der aktuellen Markierung fortfahren“ wählt, sortiert wirklich nur die " +
          "eine Spalte – und das will man fast nie.",
        "Sicher ist es, gar nicht erst zu markieren: Ein Klick in eine einzelne Zelle " +
          "der Spalte genügt, dann sortiert Excel den ganzen zusammenhängenden Bereich " +
          "und erkennt dabei die Kopfzeile. In einer Tabelle im Sinne von Modul 5 kann " +
          "der Fehler ohnehin nicht passieren – dort sortiert Excel immer die ganze " +
          "Tabelle.",
        "Und falls es doch passiert: sofort Strg + Z. Nach dem Speichern und Schließen " +
          "ist die ursprüngliche Reihenfolge nicht mehr rekonstruierbar, außer man hat " +
          "eine Spalte mit einer fortlaufenden Nummer – ein Grund, warum viele " +
          "Datenlisten eine solche mitführen.",
      ],
      keyPoints: [
        "Nie eine einzelne Spalte markieren und sortieren.",
        "Ein Klick in eine Zelle genügt – Excel erkennt den Bereich selbst.",
        "Eine Tabelle sortiert immer als Ganzes.",
        "Eine laufende Nummer als erste Spalte macht jede Sortierung umkehrbar.",
      ],
    },
    {
      id: "sortieren-anwenden",
      title: "Sortieren mit Kopfzeile",
      explanation: [
        "Die Schaltflächen für auf- und absteigend liegen unter „Daten“ und in " +
          "jeder Filterschaltfläche einer Tabelle. Excel sortiert dabei typgerecht: " +
          "Zahlen nach Größe, Datumswerte chronologisch, Text alphabetisch. Und hier " +
          "rächt sich, was in Modul 2 stand – eine als Text gespeicherte Zahlenspalte " +
          "sortiert alphabetisch, sodass die 10 vor der 9 landet.",
        "Beim Sortieren nach Text ist die Reihenfolge sprachabhängig: Ein deutsches " +
          "Excel behandelt ä wie a, ein schwedisches setzt ä ans Ende des Alphabets. " +
          "Bei Dateien, die zwischen Ländern wandern, kann eine Sortierung deshalb " +
          "unterschiedlich ausfallen.",
        "Leere Zellen landen beim Sortieren immer am Ende, egal ob auf- oder " +
          "absteigend. Das ist praktisch, um Lücken in den Daten zu finden: einmal " +
          "nach der fraglichen Spalte sortieren, und alle unvollständigen Zeilen " +
          "stehen unten beieinander.",
      ],
      keyPoints: [
        "Cursor in eine Zelle der Spalte, dann Daten → Sortieren.",
        "Leere Zellen stehen immer am Ende – gut, um Lücken zu finden.",
        "Als Text gespeicherte Zahlen sortieren alphabetisch: 10 vor 9.",
      ],
      task: {
        instruction:
          "Sortiere deine Daten nach einer Spalte – auf- oder absteigend, aber so, dass " +
          "die ganzen Zeilen mitwandern.",
        hint: "Cursor in eine einzelne Zelle der Spalte setzen, nicht die Spalte markieren. Dann Daten → Sortieren.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Der Inhalt der Zellen",
              "1.4",
              "Sieh dir deine Daten an: Steht eine Spalte durchgehend in auf- oder absteigender Reihenfolge?",
            );
          }

          const blaetter = filledSheets(mappe);
          if (blaetter.length === 0) {
            return fail(KEIN_INHALT, "Trage zuerst deine Liste ein.");
          }

          for (const blatt of blaetter) {
            for (const spalte of datenspalten(blatt)) {
              const richtung = istGeordnet(spalte.werte);
              if (richtung !== null) {
                return pass(
                  `Auf „${blatt.name}“ steht die Spalte „${spalte.kopf}“ ${richtung} sortiert ` +
                    `(${spalte.werte.length} Werte). Prüfe kurz, ob die Nachbarspalten dazu passen – ` +
                    "dann sind die Zeilen im Ganzen mitgewandert.",
                );
              }
            }
          }

          return fail(
            "Keine deiner Datenspalten steht durchgehend in auf- oder absteigender Reihenfolge.",
            "Setze den Cursor in eine einzelne Zelle der Spalte, nach der du sortieren willst, " +
              "und wähle unter „Daten“ die Sortierrichtung. Markiere die Spalte dabei nicht.",
          );
        },
      },
    },
    {
      id: "mehrstufig",
      title: "Nach mehreren Kriterien",
      explanation: [
        "Unter „Daten“ → „Sortieren“ öffnet sich ein Dialog, in dem sich mehrere " +
          "Ebenen anlegen lassen: erst nach Region, innerhalb jeder Region nach Datum, " +
          "innerhalb jedes Datums nach Betrag. Die Reihenfolge der Ebenen entscheidet, " +
          "und sie lässt sich mit den Pfeilen daneben ändern.",
        "Dasselbe Ergebnis erreicht man auch ohne Dialog, indem man in umgekehrter " +
          "Reihenfolge nacheinander sortiert: erst nach Betrag, dann nach Datum, zuletzt " +
          "nach Region. Excel sortiert stabil, das heißt: Bei gleichen Werten bleibt die " +
          "bisherige Reihenfolge erhalten. Diese Eigenschaft ist der Grund, warum das " +
          "funktioniert – und sie gilt nicht in jeder Software.",
        "Der Dialog kann zwei weitere Dinge, die kaum jemand kennt. Erstens nach Farbe " +
          "sortieren – nützlich in Kombination mit der bedingten Formatierung aus dem " +
          "Aufbauteil. Zweitens nach einer benutzerdefinierten Liste: „Klein, Mittel, " +
          "Groß“ statt alphabetisch, oder die Wochentage in ihrer natürlichen " +
          "Reihenfolge statt Dienstag vor Donnerstag vor Freitag.",
        "Über die Schaltfläche „Optionen“ im selben Dialog lässt sich außerdem von " +
          "Zeilen auf Spalten umschalten. Damit sortiert Excel die Spalten einer " +
          "Tabelle um – selten gebraucht, aber der einzige Weg, wenn die Daten " +
          "quer statt hoch liegen.",
      ],
      keyPoints: [
        "Daten → Sortieren öffnet den Dialog mit mehreren Ebenen.",
        "Excel sortiert stabil: nacheinander sortieren ergibt dasselbe Ergebnis.",
        "Der Dialog kann auch nach Farbe und nach eigener Reihenfolge sortieren.",
      ],
      task: {
        instruction:
          "Sortiere eine Liste nach zwei Kriterien – zuerst nach einer Spalte mit sich " +
          "wiederholenden Werten (etwa Region), innerhalb davon nach einer zweiten (etwa Betrag).",
        hint: "In die Liste klicken, Daten → Sortieren → „Ebene hinzufügen“ → erst Region, dann Betrag.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar("Die Zellinhalte", "1.4", "Sieh selbst nach, ob die Liste nach beiden Spalten geordnet ist.", true);
          }
          const blaetter = filledSheets(mappe);
          if (blaetter.length === 0) return fail(KEIN_INHALT, "Leg eine Liste mit Region und Betrag an.");

          for (const blatt of blaetter) {
            const spalten = datenspalten(blatt);
            for (const erste of spalten) {
              if (istGeordnet(erste.werte) === null) continue;
              // Gruppen gleicher Werte in der ersten Spalte, mindestens zwei davon mit mehreren Zeilen.
              const gruppen = new Map<string, number[]>();
              for (const zelle of erste.werte) {
                const schluessel = String(zelle.value).toLocaleLowerCase("de");
                gruppen.set(schluessel, [...(gruppen.get(schluessel) ?? []), zelle.row]);
              }
              const mehrfach = [...gruppen.values()].filter((zeilen) => zeilen.length >= 2);
              if (mehrfach.length < 2) continue;
              for (const zweite of spalten) {
                if (zweite.spalte === erste.spalte) continue;
                const innerhalb = mehrfach.every((zeilen) => {
                  const werte = zweite.werte.filter((zelle) => zeilen.includes(zelle.row));
                  return werte.length < 2 || istGeordnet(werte) !== null || new Set(werte.map((w) => w.value)).size === 1;
                });
                const gesamtGeordnet = istGeordnet(zweite.werte) !== null;
                if (innerhalb && !gesamtGeordnet) {
                  return pass(
                    `Auf „${blatt.name}“ ist nach „${erste.kopf}“ und innerhalb davon nach „${zweite.kopf}“ sortiert.`,
                  );
                }
              }
            }
          }
          return fail(
            "Keine Liste ist nach zwei Kriterien sortiert.",
            "Die erste Spalte braucht sich wiederholende Werte – sonst gibt es nichts, was die zweite Ebene ordnen könnte.",
          );
        },
      },
    },
  ],
};
